import { refinementKeyToAlgoliaNumericAttribute } from '../search-router/refinement-key-to-search-numeric-attribute';
import { resolveFirstSeenThreshold } from '../search-router/first-seen-threshold';
import { DEFAULT_TYPESENSE_SEARCH_CONFIG } from './collection-schemas';
import { buildTypesenseSortBy, parseSortFromAlgoliaIndex } from './legacy-index-name-to-collection';
import { buildTypesenseGeoFilterFromAlgoliaInsideBoundingBox } from './build-typesense-geo-filter';
import { isSrpNonFilterQueryParam } from '../search/srp-non-filter-query-params';
import { ARRAY_FACET_COUNT_FIELD } from './array-facet-count-fields';
import { WORK_MODEL_HUBS } from '../work-model/work-model-hub';
import { buildRemoteHubSortBy, type RemoteHubSortLocale } from '../work-model/remote-hub-sort';

const REMOTE_FACET_VALUE_LOCALE = new Map<string, RemoteHubSortLocale>(
  Object.entries(WORK_MODEL_HUBS.find((d) => d.key === 'remote')!.facetValues).map(([locale, value]) => [
    value.toLowerCase(),
    locale as RemoteHubSortLocale,
  ]),
);

/**
 * The locale of a remote-only `remoteness` filter ('Remote' en, 'Etätyö' fi, 'Distans' sv), or
 * null. Qualifies only when a `remoteness` group holds exactly one value and it is a Remote
 * value. Handles both Algolia shapes: a bare 'remoteness:Etätyö' string and a one-element OR
 * group ['remoteness:Etätyö']. A remote+hybrid OR group, a Hybrid-only filter or a NOT/ONLY
 * entry never qualifies. Each locale's remote value only exists in its own collection, so the
 * value says which collection is being searched.
 */
export function remoteOnlyRemotenessLocale(facetFilters: (string | string[])[] | undefined): RemoteHubSortLocale | null {
  for (const entry of facetFilters ?? []) {
    const group = Array.isArray(entry) ? entry : [entry];
    const values: string[] = [];
    for (const item of group) {
      const m = typeof item === 'string' ? item.match(/^remoteness:(.+)$/) : null;
      if (m) values.push(m[1]);
    }
    const locale = values.length === 1 ? REMOTE_FACET_VALUE_LOCALE.get(values[0].trim().toLowerCase()) : undefined;
    if (locale) return locale;
  }
  return null;
}

export interface AlgoliaLikeSearchParams {
  query?: string;
  page?: number;
  hitsPerPage?: number;
  facets?: string[];
  facetFilters?: (string | string[])[];
  numericFilters?: string[];
  filters?: string;
  /** Algolia map geo search: [[south, west, north, east]] */
  insideBoundingBox?: number[][];
  /** Overrides the default max_facet_values (100) sent to Typesense for every requested facet. */
  maxFacetValues?: number;
}

export interface TypesenseSearchParams {
  q: string;
  query_by: string;
  include_fields: string;
  per_page: number;
  page: number;
  filter_by?: string;
  sort_by?: string;
  facet_by?: string;
  max_facet_values?: number;
  drop_tokens_threshold: number;
  typo_tokens_threshold: number;
  min_len_1typo: number;
  min_len_2typo: number;
}

const NUMERIC_RULE: Record<string, 'gte' | 'lte'> = {
  salaryRangeMin: 'gte',
  // Without this, salaryRangeMax falls through to buildFilterByFromObjectFilters' generic
  // facet-string branch, which quotes it (`salaryRangeMax:="50000"`). Typesense rejects `:=`
  // with a quoted value on this int32 field ("Numerical field has an invalid comparator"),
  // 400ing the whole query. Same bug/fix as pages/index/+data.ts's own numericRule table.
  salaryRangeMax: 'lte',
  vacationWeeks: 'gte',
  companyHeadcountRangeMax: 'lte',
  // `lastSeenMin` is the deprecated alias of `firstSeenMin` (see
  // refinementKeyToAlgoliaNumericAttribute); keep both so old bookmarked URLs still resolve.
  firstSeenMin: 'gte',
  lastSeenMin: 'gte',
};

type FacetFilterMode = 'include' | 'exclude' | 'only';

function parseFacetFilterEntry(entry: string): { attribute: string; value: string; mode: FacetFilterMode } | null {
  const onlyMatch = entry.match(/^ONLY\s+([^:]+):(.+)$/);
  if (onlyMatch) {
    return { attribute: onlyMatch[1], value: onlyMatch[2], mode: 'only' };
  }
  const excludeMatch = entry.match(/^NOT\s+([^:]+):(.+)$/);
  if (excludeMatch) {
    return { attribute: excludeMatch[1], value: excludeMatch[2], mode: 'exclude' };
  }
  const includeMatch = entry.match(/^([^:]+):(.+)$/);
  if (includeMatch) {
    return { attribute: includeMatch[1], value: includeMatch[2], mode: 'include' };
  }
  return null;
}

function escapeFilterValue(value: string): string {
  return `\`${value.replace(/`/g, '\\`')}\``;
}

/** Typesense bool facets use `field:=true` without backticks (Algolia sends `field:true`). */
const TYPESENSE_BOOL_FACET_ATTRIBUTES = new Set([
  'isFinland',
  'isTyomarkkinatori',
  'isActive',
  'isOpenApplication',
  'isSummerJob',
  'isDriversLicenseRequired',
  'isRecruitmentCompany',
  'isConsultingCompany',
  'salaryIsEstimate',
]);

function buildBoolFacetFilterClause(
  attribute: string,
  value: string,
  exclude: boolean,
): string | null {
  if (value !== 'true' && value !== 'false') {
    return null;
  }
  const field = typesenseFilterAttribute(attribute);
  if (exclude) {
    return `${field}:!=${value}`;
  }
  return `${field}:=${value}`;
}

/**
 * `objectID` filters stay on `objectID`: a job document's Typesense `id` is sha256(url), not the
 * job's objectID, so the old `objectID` -> `id` alias turned every `NOT objectID:X` (similar jobs,
 * hidden/saved/viewed/applied exclusions) into a clause that matched nothing. Requires the
 * job-object-id-indexed schema migration.
 *
 * TEC-467 Bug 4: `location` is a single joined display string ("Helsinki, Finland, Tampere,
 * Finland" for a multi-city job) — filtering on it directly can never exact-match a
 * single-city value, silently dropping every multi-location job from that city's results
 * (including its own hub page). `locationList` is the real per-city array field (see
 * to-typesense-job-record.ts, which always populates it) — every `location` filter/exclude
 * atom must resolve to it instead. Display code should keep reading `location` unchanged;
 * this only affects how filter clauses are built.
 */
export function typesenseFilterAttribute(attribute: string): string {
  if (attribute === 'location') return 'locationList';
  return attribute;
}

/**
 * Remote-hub pseudo-locations map to the remoteness facet. The indexable remote hub
 * SRPs (/en/jobs/location/remote_finland etc.) must serve the full remote inventory
 * (~3k live listings), not only the jobs whose listed location is literally
 * "Remote - Finland" (~30). Each pseudo-location value only exists in its own locale's
 * collection, so the mapped remoteness value is locale-correct by construction.
 * "Remote - Europe" hubs keep literal location filtering — their intent is
 * region-scoped, not remoteness-scoped.
 */
const REMOTE_FINLAND_LOCATION_TO_REMOTENESS: Record<string, string> = {
  'Remote - Finland': 'Remote',
  'Etätyö - Suomi': 'Etätyö',
  'Distans - Finland': 'Distans',
};

// Lowercase-keyed mirror so lookups work regardless of the incoming value's casing
// (URL-derived location values are lowercase since lowercase is the canonical URL casing).
const REMOTE_FINLAND_LOCATION_TO_REMOTENESS_LOWER: Record<string, string> = Object.fromEntries(
  Object.entries(REMOTE_FINLAND_LOCATION_TO_REMOTENESS).map(([key, value]) => [key.toLowerCase(), value]),
);

/** Include-clause for a facet atom, applying the remote-hub location→remoteness mapping. */
function buildIncludeFacetClause(attribute: string, value: string): string {
  const remoteness = attribute === 'location' ? REMOTE_FINLAND_LOCATION_TO_REMOTENESS_LOWER[value.toLowerCase()] : undefined;
  if (remoteness) {
    return `remoteness:=${escapeFilterValue(remoteness)}`;
  }
  return `${typesenseFilterAttribute(attribute)}:=${escapeFilterValue(value)}`;
}

/**
 * "Only these values" clause for an array facet: the field must contain every selected
 * value AND have exactly that many elements, so nothing else is present. Typesense
 * filter_by has no array-length/set-equality operator, hence the companion Count field
 * (see ARRAY_FACET_COUNT_FIELD). Returns null for attributes that aren't array facets —
 * "only" is meaningless on a scalar facet like companyId or jobType.
 */
function buildOnlyFacetClause(attribute: string, values: string[]): string | null {
  // Look up the count field by the resolved Typesense field name (e.g. `location` ->
  // `locationList`) — ARRAY_FACET_COUNT_FIELD is keyed by the real schema field, not the
  // UI-facing attribute name.
  const countField = ARRAY_FACET_COUNT_FIELD[typesenseFilterAttribute(attribute)];
  if (!countField) return null;
  const uniqueValues = Array.from(new Set(values));
  if (uniqueValues.length === 0) return null;
  const valueClauses = uniqueValues.map((v) => buildIncludeFacetClause(attribute, v));
  return `(${valueClauses.join(' && ')} && ${countField}:=${uniqueValues.length})`;
}

function buildFacetGroupClauses(facetFilters: (string | string[])[]): string[] {
  const groups: string[] = [];
  const onlyValuesByAttribute = new Map<string, string[]>();
  // Excludes on the same field collapse into one `field:!=[a,b,...]` clause (same semantics as
  // ANDing `field:!=a && field:!=b`, far shorter). A signed-in user's hidden-job exclusions are
  // one `NOT objectID:X` entry each, and hundreds of separate clauses blew past the URL length
  // Typesense accepts (see search-typesense-documents.ts). The clause keeps the position of the
  // field's first exclude, so a single exclude renders exactly as before.
  const excludeGroupByField = new Map<string, { index: number; values: string[] }>();

  for (const entry of facetFilters) {
    if (Array.isArray(entry)) {
      // "only" doesn't fit an OR-group's semantics; only include-mode entries participate here
      // (same as exclude-mode entries were always silently dropped from OR-groups before "only" existed).
      const orClauses = entry
        .map(parseFacetFilterEntry)
        .filter(
          (p): p is NonNullable<typeof p> =>
            p !== null && p.mode === 'include' && !isSrpNonFilterQueryParam(p.attribute),
        )
        .map((p) => buildIncludeFacetClause(p.attribute, p.value));
      if (orClauses.length === 1) {
        groups.push(orClauses[0]);
      } else if (orClauses.length > 1) {
        groups.push(`(${orClauses.join(' || ')})`);
      }
      continue;
    }

    const parsed = parseFacetFilterEntry(entry);
    if (!parsed || isSrpNonFilterQueryParam(parsed.attribute)) continue;

    if (parsed.mode === 'only') {
      const values = onlyValuesByAttribute.get(parsed.attribute) ?? [];
      values.push(parsed.value);
      onlyValuesByAttribute.set(parsed.attribute, values);
      continue;
    }

    const isBoolAttribute = TYPESENSE_BOOL_FACET_ATTRIBUTES.has(parsed.attribute);
    const field = typesenseFilterAttribute(parsed.attribute);
    if (parsed.mode === 'exclude') {
      const boolClause = isBoolAttribute
        ? buildBoolFacetFilterClause(parsed.attribute, parsed.value, true)
        : null;
      if (isBoolAttribute) {
        // A crafted/garbage URL value like ?isOpenApplication=614213916 isn't 'true'/'false', so
        // buildBoolFacetFilterClause rejects it (returns null) — that must drop the filter
        // entirely, not fall back to a generic string clause: Typesense's schema declares this
        // field as bool, so any non-boolean value 400s the whole query (JOBCRAWLS-Z15).
        if (boolClause) groups.push(boolClause);
      } else {
        const existing = excludeGroupByField.get(field);
        if (existing) {
          if (!existing.values.includes(parsed.value)) existing.values.push(parsed.value);
        } else {
          excludeGroupByField.set(field, { index: groups.length, values: [parsed.value] });
          groups.push('');
        }
      }
    } else {
      const boolClause = isBoolAttribute
        ? buildBoolFacetFilterClause(parsed.attribute, parsed.value, false)
        : null;
      if (parsed.attribute === 'isFinland' && parsed.value === 'true') {
        // Työmarkkinatori jobs are always Finland; include when isFinland was missing on older index rows.
        groups.push('(isFinland:=true || isTyomarkkinatori:=true)');
      } else if (isBoolAttribute) {
        if (boolClause) groups.push(boolClause);
      } else {
        groups.push(buildIncludeFacetClause(parsed.attribute, parsed.value));
      }
    }
  }

  for (const [field, { index, values }] of excludeGroupByField) {
    groups[index] =
      values.length === 1
        ? `${field}:!=${escapeFilterValue(values[0])}`
        : `${field}:!=[${values.map(escapeFilterValue).join(',')}]`;
  }

  for (const [attribute, values] of onlyValuesByAttribute) {
    const clause = buildOnlyFacetClause(attribute, values);
    if (clause) groups.push(clause);
  }

  return groups;
}

/** Split on a separator only at the top level (outside parentheses). */
function splitTopLevel(input: string, separator: ' AND ' | ' OR '): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  let i = 0;
  while (i < input.length) {
    const ch = input[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (depth === 0 && input.startsWith(separator, i)) {
      parts.push(current);
      current = '';
      i += separator.length;
      continue;
    }
    current += ch;
    i += 1;
  }
  parts.push(current);
  return parts.map((p) => p.trim()).filter(Boolean);
}

/** Strip one layer of parentheses only when they wrap the entire expression. */
function unwrapOuterParens(input: string): string {
  let s = input.trim();
  while (s.startsWith('(') && s.endsWith(')')) {
    let depth = 0;
    let wrapsWhole = true;
    for (let i = 0; i < s.length; i += 1) {
      if (s[i] === '(') depth += 1;
      else if (s[i] === ')') depth -= 1;
      if (depth === 0 && i < s.length - 1) {
        wrapsWhole = false;
        break;
      }
    }
    if (!wrapsWhole) break;
    s = s.slice(1, -1).trim();
  }
  return s;
}

function stripQuotes(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  return trimmed;
}

/** Translate a single Algolia filter atom (e.g. `NOT objectID:x`, `location:"Y"`, `salaryRangeMin>=10`). */
function buildClauseFromFilterAtom(atom: string): string | null {
  const trimmed = atom.trim().replace(/^\((.*)\)$/s, '$1').trim();
  if (!trimmed) return null;

  const numeric = trimmed.match(/^([A-Za-z0-9_]+)\s*(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)$/);
  if (numeric) {
    const op = numeric[2] === '=' ? ':=' : `:${numeric[2]}`;
    return `${typesenseFilterAttribute(numeric[1])}${op}${numeric[3]}`;
  }

  const facet = trimmed.match(/^(NOT\s+)?([A-Za-z0-9_]+):(.+)$/s);
  if (!facet) return null;
  const exclude = Boolean(facet[1]);
  const attribute = facet[2];
  if (isSrpNonFilterQueryParam(attribute)) return null;
  const value = stripQuotes(facet[3]);
  const field = typesenseFilterAttribute(attribute);

  if (TYPESENSE_BOOL_FACET_ATTRIBUTES.has(attribute)) {
    // A non-'true'/'false' value for a bool-only field (a crafted/garbage URL param) must drop
    // the filter entirely, not fall through to a generic string clause: Typesense's schema
    // declares this field as bool, so any non-boolean value 400s the whole query (JOBCRAWLS-Z15).
    return buildBoolFacetFilterClause(attribute, value, exclude);
  }
  return exclude
    ? `${field}:!=${escapeFilterValue(value)}`
    : buildIncludeFacetClause(attribute, value);
}

/**
 * Translate an Algolia `filters` string into Typesense `filter_by` clauses.
 * Supports top-level ` AND `, parenthesised/inline ` OR ` groups, `NOT`, quoted values,
 * and numeric comparisons. Consumers that pass a raw `filters` string (similar jobs,
 * locations hub, objectID lookups) previously had it silently dropped under Typesense.
 */
function buildFilterByFromFiltersString(filters: string): string[] {
  const conjuncts = splitTopLevel(filters.trim(), ' AND ');
  const clauses: string[] = [];
  for (const conjunct of conjuncts) {
    const orParts = splitTopLevel(unwrapOuterParens(conjunct), ' OR ');
    if (orParts.length > 1) {
      const orClauses = orParts
        .map(buildClauseFromFilterAtom)
        .filter((c): c is string => c !== null);
      if (orClauses.length === 1) clauses.push(orClauses[0]);
      else if (orClauses.length > 1) clauses.push(`(${orClauses.join(' || ')})`);
      continue;
    }
    const clause = buildClauseFromFilterAtom(conjunct);
    if (clause) clauses.push(clause);
  }
  return clauses;
}

function buildNumericClauses(numericFilters: string[]): string[] {
  const clauses: string[] = [];
  for (const filter of numericFilters) {
    const gteMatch = filter.match(/^([^><=]+)>=(.+)$/);
    const lteMatch = filter.match(/^([^><=]+)<=(.+)$/);
    if (gteMatch) {
      clauses.push(`${gteMatch[1]}:>=${gteMatch[2]}`);
    } else if (lteMatch) {
      clauses.push(`${lteMatch[1]}:<=${lteMatch[2]}`);
    }
  }
  return clauses;
}

function buildFilterByFromObjectFilters(filters: Record<string, unknown>): string[] {
  const clauses: string[] = [];

  for (const [attribute, raw] of Object.entries(filters)) {
    const isExclude = attribute.startsWith('exclude_');
    const isOnly = !isExclude && attribute.startsWith('only_');
    const actualAttribute = isExclude
      ? attribute.replace('exclude_', '')
      : isOnly
        ? attribute.replace('only_', '')
        : attribute;
    const values = Array.isArray(raw) ? raw : [raw];

    if (isOnly) {
      const stringValues = values.map((v) => String(v));
      const clause = buildOnlyFacetClause(actualAttribute, stringValues);
      if (clause) clauses.push(clause);
      continue;
    }

    if (NUMERIC_RULE[actualAttribute] && !isExclude) {
      for (const v of values) {
        // firstSeenMin (and its deprecated lastSeenMin alias) also accepts stable relative codes
        // ("24h", "1w"), see first-seen-threshold.ts.
        const num = actualAttribute === 'firstSeenMin' || actualAttribute === 'lastSeenMin'
          ? (resolveFirstSeenThreshold(v as string | number) ?? NaN)
          : typeof v === 'number' ? v : Number(String(v).trim());
        if (!Number.isFinite(num)) continue;
        const tsAttribute = refinementKeyToAlgoliaNumericAttribute(actualAttribute);
        if (NUMERIC_RULE[actualAttribute] === 'gte') {
          clauses.push(`${tsAttribute}:>=${num}`);
        } else {
          clauses.push(`${tsAttribute}:<=${num}`);
        }
      }
    } else if (!NUMERIC_RULE[actualAttribute]) {
      const stringValues = values.map((v) => String(v));
      if (isExclude) {
        for (const v of stringValues) {
          clauses.push(`${actualAttribute}:!=${escapeFilterValue(v)}`);
        }
      } else if (stringValues.length === 1) {
        clauses.push(`${actualAttribute}:=${escapeFilterValue(stringValues[0])}`);
      } else if (stringValues.length > 1) {
        clauses.push(
          `(${stringValues.map((v) => `${actualAttribute}:=${escapeFilterValue(v)}`).join(' || ')})`,
        );
      }
    }
  }

  return clauses;
}

export function buildTypesenseSearchParams(
  algoliaParams: AlgoliaLikeSearchParams,
  options?: { indexName?: string; sort?: 'relevance' | 'date' | 'salary' },
): TypesenseSearchParams {
  const sort =
    options?.sort ??
    (options?.indexName ? parseSortFromAlgoliaIndex(options.indexName) : 'relevance');

  const filterClauses: string[] = [];

  if (algoliaParams.facetFilters?.length) {
    filterClauses.push(...buildFacetGroupClauses(algoliaParams.facetFilters));
  }
  if (algoliaParams.numericFilters?.length) {
    filterClauses.push(...buildNumericClauses(algoliaParams.numericFilters));
  }
  if (algoliaParams.insideBoundingBox?.length) {
    const geoFilter = buildTypesenseGeoFilterFromAlgoliaInsideBoundingBox(
      algoliaParams.insideBoundingBox,
    );
    if (geoFilter) {
      filterClauses.push(geoFilter);
    }
  }
  if (algoliaParams.filters?.trim()) {
    filterClauses.push(...buildFilterByFromFiltersString(algoliaParams.filters));
  }

  const queryText = algoliaParams.query?.trim() ?? '';
  const isGeoSearch = Boolean(algoliaParams.insideBoundingBox?.length);
  const perPage = algoliaParams.hitsPerPage ?? DEFAULT_TYPESENSE_SEARCH_CONFIG.per_page;
  // Typesense 422s the whole request ("Only upto 4294967295 hits can be fetched") when
  // page*per_page exceeds its internal uint32 hit-count limit — a scraper/bot requesting an
  // absurd ?page= value (e.g. page=338132875) crashes the SRP instead of just getting zero
  // results (JOBCRAWLS-Z1H). Clamp to the highest page Typesense will accept for this per_page.
  const TYPESENSE_MAX_FETCHABLE_HITS = 4294967295;
  const maxPage = Math.max(1, Math.floor(TYPESENSE_MAX_FETCHABLE_HITS / perPage));
  const page = Math.min(Math.max(1, (algoliaParams.page ?? 0) + 1), maxPage);

  const params: TypesenseSearchParams = {
    q: queryText || (isGeoSearch ? '*' : ''),
    query_by: DEFAULT_TYPESENSE_SEARCH_CONFIG.query_by,
    include_fields: DEFAULT_TYPESENSE_SEARCH_CONFIG.include_fields,
    per_page: perPage,
    page,
    drop_tokens_threshold: DEFAULT_TYPESENSE_SEARCH_CONFIG.drop_tokens_threshold,
    typo_tokens_threshold: DEFAULT_TYPESENSE_SEARCH_CONFIG.typo_tokens_threshold,
    min_len_1typo: DEFAULT_TYPESENSE_SEARCH_CONFIG.min_len_1typo,
    min_len_2typo: DEFAULT_TYPESENSE_SEARCH_CONFIG.min_len_2typo,
  };

  if (algoliaParams.facets?.length) {
    // `location` is a joined display string ("Espoo, Finland, Helsinki, Finland" for a
    // multi-city job) — faceting on it directly produces one bucket per unique joined
    // combination instead of one per city. Route it to `locationList` (the real per-city
    // array field) the same way filter clauses already are (see typesenseFilterAttribute
    // above); dedupe in case a caller also requested `locationList` explicitly.
    const mappedFacets = Array.from(new Set(algoliaParams.facets.map(typesenseFilterAttribute)));
    params.facet_by = mappedFacets.join(',');
    params.max_facet_values = algoliaParams.maxFacetValues ?? 100;
  }

  const sortBy = buildTypesenseSortBy(sort);
  if (sortBy && !isGeoSearch) {
    params.sort_by = sortBy;
  } else if (sort === 'relevance' && !queryText && !isGeoSearch) {
    // Remote hub default order, computed at query time: see utils/work-model/remote-hub-sort.ts.
    const remoteLocale = remoteOnlyRemotenessLocale(algoliaParams.facetFilters);
    if (remoteLocale) params.sort_by = buildRemoteHubSortBy(remoteLocale);
  }

  if (filterClauses.length > 0) {
    params.filter_by = filterClauses.join(' && ');
  }

  return params;
}

export function buildTypesenseFilterByFromObject(
  filters: Record<string, unknown>,
  options?: { isFinlandEnabled?: boolean },
): string | undefined {
  const clauses = buildFilterByFromObjectFilters(filters);
  if (options?.isFinlandEnabled) {
    clauses.push('(isFinland:=true || isTyomarkkinatori:=true)');
  }
  return clauses.length > 0 ? clauses.join(' && ') : undefined;
}
